import { addDays, differenceInCalendarDays, format, parseISO, subDays } from 'date-fns';

export type SharedPetFoodDraft = {
  brandName: string;
  flavour: string;
  packageSize: string;
  price: string;
  lastPurchaseDate: string;
  lastsForDays: string;
  refillReminderDaysBefore: string;
  notes: string;
};

export type SharedPetFoodRowInput = {
  brand_name?: string | null;
  flavour?: string | null;
  package_size?: string | null;
  price?: number | string | null;
  last_purchase_date?: string | null;
  lasts_for_days?: number | string | null;
  refill_reminder_days_before?: number | string | null;
  notes?: string | null;
};

export type PetFoodStatusTone = 'neutral' | 'positive' | 'warning' | 'danger';

export type PetFoodStatus = {
  runoutDate: string | null;
  refillDate: string | null;
  daysUntilRunout: number | null;
  tone: PetFoodStatusTone;
  headline: string;
  detail: string;
};

export const EMPTY_SHARED_PET_FOOD_DRAFT: SharedPetFoodDraft = {
  brandName: '',
  flavour: '',
  packageSize: '',
  price: '',
  lastPurchaseDate: '',
  lastsForDays: '',
  refillReminderDaysBefore: '3',
  notes: '',
};

function trimOptionalString(value: string) {
  const trimmed = value.trim();
  return trimmed.length ? trimmed : null;
}

export function parsePetFoodPrice(value: string) {
  const cleaned = value.replace(/,/g, '.').trim();
  if (!cleaned) return null;
  const parsed = Number(cleaned);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return null;
  }
  return Number(parsed.toFixed(2));
}

export function parsePositiveWholeNumber(value: string) {
  const cleaned = value.trim();
  if (!cleaned) return null;
  const parsed = Number.parseInt(cleaned, 10);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null;
  }
  return parsed;
}

export function formatPetFoodPrice(value: number | string | null | undefined) {
  if (value == null || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(parsed)) return null;
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(parsed);
}

export function buildPetFoodPayload(input: {
  userId: string;
  petId: string;
  draft: SharedPetFoodDraft;
}) {
  return {
    user_id: input.userId,
    pet_id: input.petId,
    brand_name: input.draft.brandName.trim(),
    flavour: trimOptionalString(input.draft.flavour),
    package_size: trimOptionalString(input.draft.packageSize),
    price: parsePetFoodPrice(input.draft.price),
    last_purchase_date: input.draft.lastPurchaseDate || null,
    lasts_for_days: parsePositiveWholeNumber(input.draft.lastsForDays),
    refill_reminder_days_before: parsePositiveWholeNumber(input.draft.refillReminderDaysBefore) ?? 0,
    notes: trimOptionalString(input.draft.notes),
  };
}

export function buildPetFoodDraft(input?: SharedPetFoodRowInput | null): SharedPetFoodDraft {
  if (!input) {
    return { ...EMPTY_SHARED_PET_FOOD_DRAFT };
  }

  return {
    brandName: String(input.brand_name || ''),
    flavour: String(input.flavour || ''),
    packageSize: String(input.package_size || ''),
    price: input.price == null ? '' : String(input.price),
    lastPurchaseDate: String(input.last_purchase_date || ''),
    lastsForDays: input.lasts_for_days == null ? '' : String(input.lasts_for_days),
    refillReminderDaysBefore:
      input.refill_reminder_days_before == null ? '3' : String(input.refill_reminder_days_before),
    notes: String(input.notes || ''),
  };
}

export function buildPetFoodSummary(input?: SharedPetFoodRowInput | null) {
  if (!input?.brand_name) return [];
  return [
    input.brand_name,
    input.flavour || null,
    input.package_size || null,
    formatPetFoodPrice(input.price),
    input.lasts_for_days ? `Lasts about ${input.lasts_for_days} day${Number(input.lasts_for_days) === 1 ? '' : 's'}` : null,
  ].filter(Boolean) as string[];
}

export function buildPetFoodStatus(input?: SharedPetFoodRowInput | null): PetFoodStatus {
  if (!input?.last_purchase_date || !input?.lasts_for_days) {
    return {
      runoutDate: null,
      refillDate: null,
      daysUntilRunout: null,
      tone: 'neutral',
      headline: 'Food timing not set yet',
      detail: 'Add the last purchase date and roughly how long it lasts so Adulting can flag the next refill window.',
    };
  }

  const lastsForDays = typeof input.lasts_for_days === 'number'
    ? input.lasts_for_days
    : Number.parseInt(String(input.lasts_for_days), 10);
  const reminderLead = input.refill_reminder_days_before == null
    ? 3
    : typeof input.refill_reminder_days_before === 'number'
      ? input.refill_reminder_days_before
      : Number.parseInt(String(input.refill_reminder_days_before), 10);

  if (!Number.isFinite(lastsForDays) || lastsForDays <= 0) {
    return {
      runoutDate: null,
      refillDate: null,
      daysUntilRunout: null,
      tone: 'neutral',
      headline: 'Food timing not set yet',
      detail: 'Add the last purchase date and roughly how long it lasts so Adulting can flag the next refill window.',
    };
  }

  const runoutDate = format(addDays(parseISO(input.last_purchase_date), lastsForDays), 'yyyy-MM-dd');
  const refillDate = format(subDays(parseISO(runoutDate), Math.max(0, reminderLead || 0)), 'yyyy-MM-dd');
  const daysUntilRunout = differenceInCalendarDays(parseISO(runoutDate), new Date());

  if (daysUntilRunout < 0) {
    return {
      runoutDate,
      refillDate,
      daysUntilRunout,
      tone: 'danger',
      headline: `Food likely ran out ${Math.abs(daysUntilRunout)} day${Math.abs(daysUntilRunout) === 1 ? '' : 's'} ago`,
      detail: `Adulting starts flagging this from ${format(parseISO(refillDate), 'd MMM yyyy')}.`,
    };
  }

  if (daysUntilRunout === 0) {
    return {
      runoutDate,
      refillDate,
      daysUntilRunout,
      tone: 'danger',
      headline: 'Food likely runs out today',
      detail: `Adulting starts flagging this from ${format(parseISO(refillDate), 'd MMM yyyy')}.`,
    };
  }

  if (daysUntilRunout <= Math.max(0, reminderLead || 0)) {
    return {
      runoutDate,
      refillDate,
      daysUntilRunout,
      tone: 'warning',
      headline: `Refill due in ${daysUntilRunout} day${daysUntilRunout === 1 ? '' : 's'}`,
      detail: `Expected runout: ${format(parseISO(runoutDate), 'd MMM yyyy')}.`,
    };
  }

  return {
    runoutDate,
    refillDate,
    daysUntilRunout,
    tone: 'positive',
    headline: `Food looks covered for ${daysUntilRunout} more day${daysUntilRunout === 1 ? '' : 's'}`,
    detail: `Expected runout: ${format(parseISO(runoutDate), 'd MMM yyyy')}. Adulting flags a refill from ${format(parseISO(refillDate), 'd MMM yyyy')}.`,
  };
}

export function buildPetFoodSignal(input?: SharedPetFoodRowInput | null) {
  const status = buildPetFoodStatus(input);
  if (!input?.brand_name) {
    return 'Food profile not set';
  }
  return status.headline;
}
